import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { Activity, Database, Cpu, Wifi, RefreshCw, AlertCircle, Mail, Send } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toastSuccess, toastError, toastInfo } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider, SystemHealth } from '../types/dataProvider';
import { AuthService } from '../services/AuthService';
import { buildDetails } from '../version';

/* ============================================================
   System health — Latio design system (derived). Live service status.
   ============================================================ */

const POLL_INTERVAL_MS = 30_000;

const statusBadgeVariant = (status?: string): 'default' | 'destructive' | 'secondary' => {
  if (!status) return 'secondary';
  return status.toLowerCase() === 'healthy' ? 'default' : 'destructive';
};

const CheckCard: React.FC<{
  title: string;
  icon: React.ElementType;
  status?: string;
  statusBadge?: React.ReactNode;
  detail?: React.ReactNode;
  note?: string;
  action?: React.ReactNode;
}> = ({ title, icon: Icon, status, statusBadge, detail, note, action }) => (
  <div className="rounded-card border border-hairline bg-background p-5">
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-latio-sm bg-surface-2 text-ink-2">
          <Icon className="size-5" />
        </span>
        <h3 className="text-[15px] font-bold text-ink">{title}</h3>
      </div>
      {statusBadge ? statusBadge : <Badge variant={statusBadgeVariant(status)}>{status || 'Unknown'}</Badge>}
    </div>
    {detail && <div className="text-sm text-ink">{detail}</div>}
    {note && <p className="mt-2 text-xs text-ink-3">{note}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

interface EmailHealth {
  label: string;
  variant: 'default' | 'destructive' | 'secondary';
  degraded: boolean;
}
function deriveEmailHealth(data?: SystemHealth | null): EmailHealth {
  if (!data) return { label: 'Unknown', variant: 'secondary', degraded: false };
  const auth = data.email_auth_status?.toLowerCase();
  const reachable = data.email_smtp_reachable;
  if (auth === 'ok') return { label: 'Healthy', variant: 'default', degraded: false };
  if (auth === 'bad_credentials') return { label: 'Auth failed', variant: 'destructive', degraded: true };
  if (auth === 'unconfigured' || reachable === null) return { label: 'Not configured', variant: 'secondary', degraded: false };
  if (reachable === false) return { label: 'Unreachable', variant: 'destructive', degraded: true };
  return { label: 'Unknown', variant: 'secondary', degraded: false };
}

export function SystemHealthPage() {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [data, setData] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentUser = AuthService.getCurrentUser();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipient, setRecipient] = useState<string>(currentUser?.email || '');
  const [sending, setSending] = useState(false);

  const handleSendTestEmail = useCallback(async () => {
    if (!recipient) return;
    setSending(true);
    try {
      const { data: result } = await dataProvider.sendTestEmail({ recipient });
      if (result?.status === 'dev_mode') toastInfo('Dev mode — email logged to console (MAIL_FROM not set)');
      else toastSuccess(`Test email sent to ${recipient}`);
      setEmailDialogOpen(false);
    } catch (e: any) {
      toastError(e?.message || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  }, [dataProvider, recipient]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getSystemHealth();
      setData(data);
      setLastFetched(new Date());
    } catch (e: any) {
      setError(e?.message || 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, [dataProvider]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, load]);

  const emailHealth = deriveEmailHealth(data);
  const overallHealthy =
    data?.database_status?.toLowerCase() === 'healthy' &&
    data?.redis_status?.toLowerCase() === 'healthy' &&
    !emailHealth.degraded;

  return (
    <div className="space-y-6 p-6" style={{ maxWidth: 1100 }}>
      <div>
        <h1 className="flex items-center gap-2 text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
          <Activity className="size-6 text-ink-2" />
          System health
        </h1>
        <p className="mt-1 text-sm text-ink-2">Live status of backend services and infrastructure.</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-hairline bg-background p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={overallHealthy ? 'default' : 'destructive'}>
            {data ? (overallHealthy ? 'All systems healthy' : 'Degraded') : 'Loading…'}
          </Badge>
          {lastFetched && <span className="text-xs text-ink-2">Last checked: {lastFetched.toLocaleTimeString()}</span>}
          {data?.timestamp && <span className="text-xs text-ink-2">Backend: {new Date(data.timestamp).toLocaleString()}</span>}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch id="system-health-auto-refresh" checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            <Label htmlFor="system-health-auto-refresh" className="text-sm text-ink-2">Auto-refresh {POLL_INTERVAL_MS / 1000}s</Label>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
          >
            <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
            Refresh now
          </button>
        </div>
      </div>

      {/* The dashboard's own identity. Everything else on this page reports the
          backend; this answers "which build of the dashboard am I looking at?",
          which is the question nobody could answer while QA quietly served a
          2026-06-20 bundle for two and a half months. */}
      <div className="rounded-card border border-hairline bg-background p-4">
        <h2 className="text-sm font-semibold text-ink">Dashboard build</h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
          {buildDetails().map(({ label, value }) => (
            <div key={label}>
              <dt className="text-xs text-ink-2">{label}</dt>
              <dd className="mt-0.5 break-all font-mono text-sm text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-card border border-error/30 bg-error-bg p-4 text-sm text-error">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <CheckCard title="Database" icon={Database} status={data?.database_status} detail={<span className="text-ink-2">Primary application database connectivity.</span>} />
        <CheckCard title="Redis" icon={Cpu} status={data?.redis_status} detail={<span className="text-ink-2">Cache and pub/sub layer.</span>} />
        <CheckCard
          title="Email (SMTP)"
          icon={Mail}
          statusBadge={<Badge variant={emailHealth.variant}>{emailHealth.label}</Badge>}
          detail={<span className="text-ink-2">{data?.email_detail || 'Outbound email service reachability.'}</span>}
          action={
            <button
              type="button"
              onClick={() => setEmailDialogOpen(true)}
              disabled={!data}
              className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
            >
              <Send className="size-4" />
              Send test email
            </button>
          }
        />
        <CheckCard
          title="WebSockets"
          icon={Wifi}
          status={data ? 'healthy' : undefined}
          detail={
            <div>
              <div className="text-[24px] font-bold text-ink">{data?.active_websocket_connections ?? '—'}</div>
              <div className="mt-1 text-xs text-ink-3">Active connections</div>
            </div>
          }
          note="Note: backend currently returns 0 until accurate counting is wired."
        />
      </div>

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send test email</DialogTitle>
            <DialogDescription>Sends a test email via the backend SMTP configuration to verify deliverability.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="test-email-recipient">Recipient</Label>
            <Input id="test-email-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="you@example.com" />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setEmailDialogOpen(false)}
              disabled={sending}
              className="inline-flex h-9 items-center rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSendTestEmail}
              disabled={sending || !recipient}
              className="inline-flex h-9 items-center gap-2 rounded-pill bg-black px-4 text-sm font-semibold text-white hover:bg-ink disabled:opacity-50"
            >
              {sending ? <RefreshCw className="size-4 animate-spin" /> : <Send className="size-4" />}
              {sending ? 'Sending…' : 'Send'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
